import React, { createContext, useContext, useState, ReactNode } from "react";

// Loading state interface
interface LoadingState {
  isLoading: boolean;
  message?: string;
}

// Dialog types
export type DialogType = "info" | "warning" | "error" | "success" | "confirm";

// Dialog configuration interface
export interface DialogConfig {
  id: string;
  type: DialogType;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  showCancel?: boolean;
  autoClose?: number; // Auto close after X milliseconds
}

// Base context interface
interface BaseContextType {
  // Loading state
  loading: LoadingState;
  showLoading: (message?: string) => void;
  hideLoading: () => void;

  // Dialog management
  dialogs: DialogConfig[];
  showDialog: (config: Omit<DialogConfig, "id">) => string;
  hideDialog: (id: string) => void;
  hideAllDialogs: () => void;

  // Convenience methods for different dialog types
  showInfo: (
    title: string,
    message: string,
    options?: Partial<DialogConfig>
  ) => string;
  showWarning: (
    title: string,
    message: string,
    options?: Partial<DialogConfig>
  ) => string;
  showError: (
    title: string,
    message: string,
    options?: Partial<DialogConfig>
  ) => string;
  showSuccess: (
    title: string,
    message: string,
    options?: Partial<DialogConfig>
  ) => string;
  showConfirm: (
    title: string,
    message: string,
    onConfirm: () => void | Promise<void>,
    options?: Partial<DialogConfig>
  ) => string;
}

// Create the context
const BaseContext = createContext<BaseContextType | undefined>(undefined);

// Base provider props
interface BaseProviderProps {
  children: ReactNode;
}

// Generate unique ID for dialogs
const generateId = () =>
  Date.now().toString(36) + Math.random().toString(36).substr(2);

// Base provider component
export const BaseProvider: React.FC<BaseProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<LoadingState>({ isLoading: false });
  const [dialogs, setDialogs] = useState<DialogConfig[]>([]);

  // Loading functions
  const showLoading = (message?: string) => {
    setLoading({ isLoading: true, message });
  };

  const hideLoading = () => {
    setLoading({ isLoading: false });
  };

  // Dialog functions
  const showDialog = (config: Omit<DialogConfig, "id">): string => {
    const id = generateId();
    const newDialog: DialogConfig = {
      id,
      showCancel: config.type === "confirm",
      confirmText: "OK",
      cancelText: "Cancel",
      ...config,
    };

    setDialogs((prev) => [...prev, newDialog]);

    // Auto close if specified
    if (newDialog.autoClose) {
      setTimeout(() => {
        hideDialog(id);
      }, newDialog.autoClose);
    }

    return id;
  };

  const hideDialog = (id: string) => {
    setDialogs((prev) => prev.filter((dialog) => dialog.id !== id));
  };

  const hideAllDialogs = () => {
    setDialogs([]);
  };

  // Convenience methods
  const showInfo = (
    title: string,
    message: string,
    options?: Partial<DialogConfig>
  ): string => {
    return showDialog({
      type: "info",
      title,
      message,
      ...options,
    });
  };

  const showWarning = (
    title: string,
    message: string,
    options?: Partial<DialogConfig>
  ): string => {
    return showDialog({
      type: "warning",
      title,
      message,
      ...options,
    });
  };

  const showError = (
    title: string,
    message: string,
    options?: Partial<DialogConfig>
  ): string => {
    return showDialog({
      type: "error",
      title,
      message,
      ...options,
    });
  };

  const showSuccess = (
    title: string,
    message: string,
    options?: Partial<DialogConfig>
  ): string => {
    return showDialog({
      type: "success",
      title,
      message,
      autoClose: 3000, // Auto close success messages after 3 seconds
      ...options,
    });
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void | Promise<void>,
    options?: Partial<DialogConfig>
  ): string => {
    return showDialog({
      type: "confirm",
      title,
      message,
      onConfirm,
      showCancel: true,
      ...options,
    });
  };

  const value: BaseContextType = {
    loading,
    showLoading,
    hideLoading,
    dialogs,
    showDialog,
    hideDialog,
    hideAllDialogs,
    showInfo,
    showWarning,
    showError,
    showSuccess,
    showConfirm,
  };

  return (
    <BaseContext.Provider value={value}>
      {children}
      <LoadingOverlay />
      <DialogContainer />
    </BaseContext.Provider>
  );
};

// Loading overlay component
const LoadingOverlay: React.FC = () => {
  const { loading } = useBase();

  if (!loading.isLoading) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4 shadow-xl">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-700 font-medium">
          {loading.message || "Loading..."}
        </p>
      </div>
    </div>
  );
};

// Dialog container component
const DialogContainer: React.FC = () => {
  const { dialogs, hideDialog } = useBase();

  if (dialogs.length === 0) return null;

  return (
    <>
      {dialogs.map((dialog) => (
        <DialogModal
          key={dialog.id}
          dialog={dialog}
          onClose={() => hideDialog(dialog.id)}
        />
      ))}
    </>
  );
};

// Individual dialog modal component
interface DialogModalProps {
  dialog: DialogConfig;
  onClose: () => void;
}

const DialogModal: React.FC<DialogModalProps> = ({ dialog, onClose }) => {
  const handleConfirm = async () => {
    if (dialog.onConfirm) {
      try {
        await dialog.onConfirm();
      } catch (error) {
        console.error("Dialog confirm error:", error);
      }
    }
    onClose();
  };

  const handleCancel = () => {
    if (dialog.onCancel) {
      dialog.onCancel();
    }
    onClose();
  };

  // Dialog type styling
  const getDialogStyles = (type: DialogType) => {
    switch (type) {
      case "error":
        return {
          iconColor: "text-red-600",
          iconBg: "bg-red-100",
          icon: (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          ),
        };
      case "warning":
        return {
          iconColor: "text-yellow-600",
          iconBg: "bg-yellow-100",
          icon: (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          ),
        };
      case "success":
        return {
          iconColor: "text-green-600",
          iconBg: "bg-green-100",
          icon: (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
        };
      case "confirm":
        return {
          iconColor: "text-blue-600",
          iconBg: "bg-blue-100",
          icon: (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
        };
      default: // info
        return {
          iconColor: "text-blue-600",
          iconBg: "bg-blue-100",
          icon: (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
        };
    }
  };

  const styles = getDialogStyles(dialog.type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-full ${styles.iconBg} flex items-center justify-center`}
            >
              <div className={styles.iconColor}>{styles.icon}</div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {dialog.title}
              </h3>
              <p className="text-sm text-gray-600">{dialog.message}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3 rounded-b-lg">
          {dialog.showCancel && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {dialog.cancelText}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {dialog.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Custom hook to use base context
export const useBase = (): BaseContextType => {
  const context = useContext(BaseContext);
  if (context === undefined) {
    throw new Error("useBase must be used within a BaseProvider");
  }
  return context;
};

export default BaseContext;
